import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import JobPosting from './JobPosting';
import MyJobs from './MyJobs';
import ReviewForm from './ReviewForm';

const Profile = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(user || {});
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch profile, orders, reviews, and subscription status
      const fetchPromises = [
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/customer/${user?.id || profile?.id}`, { headers })
      ];

      // Add subscription status for providers
      if (user?.userType === 'provider' || profile?.userType === 'provider') {
        fetchPromises.push(
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, { headers })
        );
      }

      const responses = await Promise.all(fetchPromises);
      const [profileRes, ordersRes, reviewsRes, subscriptionRes] = responses;

      if (profileRes.ok && ordersRes.ok) {
        const [profileData, ordersData] = await Promise.all([
          profileRes.json(),
          ordersRes.json()
        ]);

        setProfile(profileData);
        setOrders(ordersData);
        
        // Reviews might not exist for new users
        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(reviewsData);
        }

        // Subscription status for providers
        if (subscriptionRes && subscriptionRes.ok) {
          const subscriptionData = await subscriptionRes.json();
          setSubscriptionStatus(subscriptionData);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          address: profile.address,
          userType: profile.userType,
          // Note: company field is not included as it should not be changeable for providers
        }),
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        toast({
          title: "Profile updated",
          description: "Your profile has been successfully updated.",
        });
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please select a JPEG, PNG, or WebP image.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    setUploadingPicture(true);

    try {
      const token = localStorage.getItem('dcTech_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const newProfilePictureUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        
        setProfile({...profile, profilePicture: newProfilePictureUrl});
        
        toast({
          title: "Success",
          description: "Profile picture updated successfully!",
        });
        
        // Refresh user data
        await fetchUserData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload profile picture');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload profile picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingPicture(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'confirmed': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      case 'shipped': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-white/80">Welcome, {user?.name}</span>
              
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/cart">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Cart
                </Button>
              </Link>
              
              {/* Direct Navigation Dropdown - Consistent Style */}
              <div className="relative group">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Navigate
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible  z-50">
                  <div className="py-2">
                    <Link to="/dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🏠 Main Dashboard
                    </Link>
                    <Link to="/inbox" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      📨 Messages
                    </Link>
                    <Link to="/jobs/manage" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🔧 My Jobs
                    </Link>
                    {(user?.userType === 'provider' || user?.userType === 'admin') && (
                      <Link to="/provider-dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        📊 Provider Dashboard
                      </Link>
                    )}
                    {(user?.userType === 'admin' || user?.userType === 'supervisor') && (
                      <Link to={user?.userType === 'admin' ? '/admin' : '/supervisor-dashboard'} className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        {user?.userType === 'admin' ? '⚙️ Admin Panel' : '👥 Supervisor Panel'}
                      </Link>
                    )}
                    {user?.userType === 'admin' && (
                      <Link to="/supervisor-dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        👥 Supervisor View
                      </Link>
                    )}
                    <div className="border-t border-white/20 my-2"></div>
                    <button 
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-red-400 bg-red-500/20 transition-colors"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold text-white mb-8">My Profile</h1>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-slate-800/80 mb-8">
            <TabsTrigger value="profile" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              👤 Profile Settings
            </TabsTrigger>
            <TabsTrigger value="orders" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              📦 My Orders
            </TabsTrigger>
            <TabsTrigger value="reviews" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              ⭐ My Reviews
            </TabsTrigger>
            <TabsTrigger value="jobs" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              💼 Job Bids
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Profile Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={updateProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Full Name</Label>
                      <Input
                        id="name"
                        type="text"
                        value={profile.name || ''}
                        onChange={(e) => setProfile({...profile, name: e.target.value})}
                        className="bg-white/10 border-white/30 text-white placeholder-white/50"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile.email || ''}
                        onChange={(e) => setProfile({...profile, email: e.target.value})}
                        className="bg-white/10 border-white/30 text-white placeholder-white/50"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={profile.phone || ''}
                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                        className="bg-white/10 border-white/30 text-white placeholder-white/50"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="userType" className="text-white">Account Type</Label>
                      <div className="flex gap-2">
                        <Badge 
                          variant={profile.userType === 'customer' ? 'default' : 'outline'}
                          className={`${
                            profile.userType === 'customer' 
                              ? 'bg-blue-500 text-white' 
                              : 'border-white/30 text-white'
                          }`}
                        >
                          Customer
                        </Badge>
                        <Badge 
                          variant={profile.userType === 'provider' ? 'default' : 'outline'}
                          className={`${
                            profile.userType === 'provider' 
                              ? 'bg-purple-500 text-white' 
                              : 'border-white/30 text-white'
                          }`}
                        >
                          Service Provider
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-white">Address</Label>
                    <Input
                      id="address"
                      type="text"
                      value={profile.address || ''}
                      onChange={(e) => setProfile({...profile, address: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50"
                      placeholder="123 Main St, City, State 12345"
                    />
                  </div>

                  {/* Profile Picture Upload Section */}
                  <div className="space-y-4">
                    <Label className="text-white text-lg">Profile Picture</Label>
                    <div className="flex items-center space-x-4">
                      {profile.profilePicture ? (
                        <div className="relative">
                          <img
                            src={profile.profilePicture}
                            alt="Profile"
                            className="w-20 h-20 rounded-full object-cover border-2 border-white/30"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
                          <span className="text-white/50 text-2xl">👤</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="profilePicture"
                          accept="image/*"
                          onChange={handleProfilePictureUpload}
                          className="hidden"
                          disabled={uploadingPicture}
                        />
                        <label
                          htmlFor="profilePicture"
                          className={`inline-flex items-center px-4 py-2 border border-white/30 rounded-md text-white bg-white/10 bg-white/10 cursor-pointer transition-colors ${
                            uploadingPicture ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingPicture ? 'Uploading...' : 'Choose Picture'}
                        </label>
                        <p className="text-white/60 text-sm mt-2">
                          JPEG, PNG, or WebP. Max 5MB.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Status Section - Only for Providers */}
                  {profile.userType === 'provider' && (
                    <div className="space-y-4">
                      <Label className="text-white text-lg">Subscription Plan</Label>
                      {subscriptionStatus && subscriptionStatus.planName ? (
                        <div className="bg-white/5 border border-white/20 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center space-x-2">
                                <Badge className="bg-green-500 text-white">
                                  Active
                                </Badge>
                                <span className="text-white font-semibold text-lg">
                                  {subscriptionStatus.planName}
                                </span>
                              </div>
                              <p className="text-white/70 mt-1">
                                ${subscriptionStatus.planPrice}/month
                              </p>
                              {subscriptionStatus.renewsAt && (
                                <p className="text-white/60 text-sm mt-2">
                                  Renews on: {new Date(subscriptionStatus.renewsAt).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-white/60 text-sm">
                                Plan cannot be changed here
                              </p>
                              <p className="text-white/60 text-sm">
                                Contact support for changes
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white/5 border border-white/20 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <Badge variant="outline" className="border-yellow-500 text-yellow-500">
                                No Active Plan
                              </Badge>
                              <p className="text-white/70 mt-1">
                                Subscribe to access provider features
                              </p>
                            </div>
                            <Link to="/subscription">
                              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                View Plans
                              </Button>
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {profile.userType === 'provider' && (
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-white">Company Name</Label>
                      <Input
                        id="company"
                        type="text"
                        value={profile.company || ''}
                        readOnly={true}
                        disabled={true}
                        className="bg-gray-600/20 border-gray-500/30 text-gray-300 placeholder-gray-400 cursor-not-allowed"
                        placeholder="Your Business Name (Set during registration)"
                      />
                      <p className="text-gray-400 text-xs">
                        ℹ️ Business name cannot be changed after registration. Contact support at <a href="mailto:Corporate.dccorp@gmail.com" className="text-blue-400 hover:text-blue-300">Corporate.dccorp@gmail.com</a> if you need to update this.
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500   text-white"
                    disabled={updating}
                  >
                    {updating ? "Updating..." : "Update Profile"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders">
            <div className="space-y-4">
              {orders.length === 0 ? (
                <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                  <CardContent className="p-8 text-center">
                    <div className="text-6xl mb-6">📦</div>
                    <h2 className="text-2xl font-bold text-white mb-4">No orders yet</h2>
                    <p className="text-white/70 mb-6">Start shopping for equipment or book services across all trades!</p>
                    <Link to="/dashboard">
                      <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                        Browse Services & Products
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                orders.map(order => (
                  <Card key={order._id} className="bg-white/10 backdrop-blur-lg border-white/20">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-white">
                          Order #{order._id?.slice(-8)}
                        </CardTitle>
                        <Badge className={`${getStatusColor(order.status)} text-white`}>
                          {order.status.toUpperCase()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-white/70">Type:</span>
                          <span className="text-white capitalize">{order.type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Total:</span>
                          <span className="text-green-400 font-bold">${order.total}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Date:</span>
                          <span className="text-white">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {order.scheduledDate && (
                          <div className="flex justify-between">
                            <span className="text-white/70">Scheduled:</span>
                            <span className="text-white">
                              {new Date(order.scheduledDate).toLocaleDateString()} at {order.scheduledTime}
                            </span>
                          </div>
                        )}
                        {order.trackingNumber && (
                          <div className="flex justify-between">
                            <span className="text-white/70">Tracking:</span>
                            <span className="text-blue-400">{order.trackingNumber}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <div className="space-y-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">My Reviews & Ratings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/70 mb-6">
                    Leave reviews for services and products you've purchased to help other customers make informed decisions.
                  </p>
                  
                  {/* Orders eligible for review */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white mb-4">Orders Available for Review</h3>
                    {orders.filter(order => order.status === 'completed').length === 0 ? (
                      <div className="text-center py-8 text-white/60">
                        <p>No completed orders available for review yet.</p>
                      </div>
                    ) : (
                      orders
                        .filter(order => order.status === 'completed')
                        .map(order => (
                          <Card key={order.id} className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-white">
                                    {order.type === 'service' ? 'Service Order' : 'Product Order'}
                                  </p>
                                  <p className="text-sm text-white/70">
                                    Order #{order.id?.slice(-8).toUpperCase()}
                                  </p>
                                  <p className="text-sm text-white/70">
                                    Completed: {new Date(order.updatedAt).toLocaleDateString()}
                                  </p>
                                  <div className="mt-2">
                                    {order.items.map((item, index) => (
                                      <p key={index} className="text-sm text-white">
                                        • {item.name} - ${item.price}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                                <Button
                                  onClick={() => setShowReviewForm(order.id)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  Write Review
                                </Button>
                              </div>
                              
                              {/* Review Form */}
                              {showReviewForm === order.id && (
                                <div className="mt-6 border-t border-white/20 pt-6">
                                  <ReviewForm
                                    orderId={order.id}
                                    orderType={order.type}
                                    onSubmitSuccess={(review) => {
                                      setShowReviewForm(null);
                                      toast({
                                        title: "Review Submitted",
                                        description: "Your review has been submitted for approval.",
                                      });
                                    }}
                                    onCancel={() => setShowReviewForm(null)}
                                  />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="jobs">
            {profile.userType === 'customer' ? (
              // Job posting and management for customers
              <Tabs defaultValue="post" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-slate-800/80 mb-6">
                  <TabsTrigger value="post" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                    ➕ Post New Job
                  </TabsTrigger>
                  <TabsTrigger value="manage" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                    📋 Manage Jobs
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="post">
                  <JobPosting />
                </TabsContent>

                <TabsContent value="manage">
                  <MyJobs />
                </TabsContent>
              </Tabs>
            ) : (
              // Message for service providers
              <div className="text-center py-12">
                <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-8 max-w-2xl mx-auto">
                  <h3 className="text-2xl font-semibold text-blue-300 mb-4">
                    💼 Service Provider Job Management
                  </h3>
                  <p className="text-blue-200 mb-6">
                    As a service provider, you can view and respond to live job bids from customers, but you cannot create job postings.
                  </p>
                  <div className="bg-white/5 p-4 rounded-lg mb-6">
                    <h4 className="text-white font-medium mb-2">📍 How to Find Work:</h4>
                    <ul className="text-left text-gray-300 space-y-2">
                      <li>• Go to Dashboard → Live Bids tab</li>
                      <li>• Browse job postings from customers</li>
                      <li>• Filter by your trade specialization</li>
                      <li>• Submit bids on jobs that match your skills</li>
                    </ul>
                  </div>
                  <Button 
                    className="bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => window.location.href = '/dashboard'}
                  >
                    View Live Bids
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;